import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import { space } from '@sunwebgroup/ui-components'
import { Button, IndeterminateProgressCircle } from '@sunwebgroup/ui-components/uds'
import { use, useEffect } from 'react'
import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { CatalogueDetailsContext } from '@/scopes/common/components/CatalogueDetailsContext'
import { useParticipantsFilter } from '@/scopes/common/components/Filters/Participants/hooks'
import {
  ChildInfo,
  ChildWithoutAdultRoomInfo,
  GroupLink,
} from '@/scopes/common/components/Filters/Participants/ParticipantsBanner'
import { useDependencies } from '@/scopes/common/infrastructure/dependencies'
import { Filter } from '@/scopes/search/components/QuickSearchBar/Filter'
import { FilterSummary } from '@/scopes/search/components/QuickSearchBar/FilterSummary'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { getGroupsLink } from '@/scopes/search/domain/policies/groupsLink'
import { useValueFilter } from '@/scopes/search/search/domain/hooks'
import * as ParticipantsFilterPrimitive from '../../common/components/Filters/Participants'

const Header = styled(ParticipantsFilterPrimitive.DialogBannerWrapper)`
  padding: ${space('primitives_space_16')};
`

const Body = styled(ParticipantsFilterPrimitive.Body)`
  padding-left: ${space('primitives_space_16')};
  padding-right: ${space('primitives_space_16')};
  padding-bottom: ${space('primitives_space_16')};

  &:first-child {
    padding-top: ${space('primitives_space_16')};
  }
`

function ParticipantsFilterComponent({
  disabled,
  setSelection,
  defaultDistribution,
  id,
  asSheet,
  biggerBreakpoint,
  isMultiRoom,
}: {
  setSelection: (selection: RoomsParticipantDistribution) => void
  disabled?: boolean
  defaultDistribution?: RoomsParticipantDistribution
  id: string
  asSheet?: boolean
  isMultiRoom: boolean
  biggerBreakpoint?: boolean
}) {
  const intl = useIntl()
  const { setActiveItem } = useQuickSearchStore()

  const {
    handleConfirm: handleConfirmFilter,
    getFormattedText,
    showParticipants,
    showChildren,
    warningNoBabiesWithoutAdults,
  } = useParticipantsFilter({
    isFetching: disabled ?? false,
    setSelection,
    isFlightOnly: !isMultiRoom,
  })
  const disabledSaveAction = warningNoBabiesWithoutAdults
  const disabledCloseButton = disabled || disabledSaveAction
  const { roomsParticipantsDistributionStore } = useDependencies()
  const { publicationCode } = use(CatalogueDetailsContext)
  const groupsLink = getGroupsLink(publicationCode)

  const showGroupsLink = groupsLink && showParticipants

  useEffect(() => {
    if (!defaultDistribution)
      return
    roomsParticipantsDistributionStore.setState({
      roomsParticipantDistribution: defaultDistribution,
    })
  }, [defaultDistribution])

  function handleConfirm() {
    setActiveItem(null)
    if (disabledSaveAction) {
      return
    }
    handleConfirmFilter()
  }

  return (
    <Filter
      id={id}
      placeholder={intl.formatMessage({
        id: 'quicksearch_filter_participants_title',
      })}
      title={intl.formatMessage({
        id: 'quicksearch_filter_participants_title',
      })}
      onConfirm={handleConfirm}
      selectionFormattedText={getFormattedText().desktop}
      asSheet={asSheet}
      hasPadding={false}
      hasBodyOverflow={false}
      biggerBreakpoint={biggerBreakpoint}
      data-qa="participants-filter"
      autoFocus
    >
      {(showChildren || showGroupsLink || warningNoBabiesWithoutAdults) && (
        <Header>
          {showChildren && !warningNoBabiesWithoutAdults && <ChildInfo />}
          {warningNoBabiesWithoutAdults && <ChildWithoutAdultRoomInfo />}
          {showGroupsLink && <GroupLink />}
        </Header>
      )}
      <Body isMultiRoom={isMultiRoom} />
      <ParticipantsFilterPrimitive.Footer>
        <Button fullWidth onClick={handleConfirm} disabled={disabledCloseButton}>
          {disabled && <IndeterminateProgressCircle />}
          {intl.formatMessage({
            id: 'quicksearch_save_button_text',
          })}
        </Button>
      </ParticipantsFilterPrimitive.Footer>
    </Filter>
  )
}

function ParticipantsFilterSummary() {
  const { isFetching, setSelection } = useValueFilter('Participants')
  const { getFormattedText } = useParticipantsFilter({
    isFetching,
    setSelection,
  })

  const { desktop: long, mobile: short } = getFormattedText()

  return <FilterSummary icon="Family" texts={{ long, short }} />
}

export { ParticipantsFilterComponent, ParticipantsFilterSummary }
